import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { loginUser } from '../userSlice';
import {
  closeAccountModal,
  selectAccountModal,
  setMode,
  setPendingEmail,
} from '../accountModalSlice';
import GoogleButton, { isGoogleLoginEnabled } from './GoogleButton';
import PasswordField from './PasswordField';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const ERROR_KEYS: Record<string, string> = {
  invalid_credentials: 'auth.errors.invalid_credentials',
  server: 'auth.errors.server',
  network: 'auth.errors.network',
  unknown: 'auth.errors.unknownLogin',
};

export default function LoginPanel() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { restaurantUuid } = useParams<{ restaurantUuid: string }>();
  const { pendingEmail, redirectTo } = useAppSelector(selectAccountModal);
  const [email, setEmail] = useState(pendingEmail ?? '');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [banner, setBanner] = useState<string | null>(null);
  // SC-777: an account created with Google has no password, so this form can
  // only ever answer "wrong email or password" to it — and the customer has no
  // way to guess why. The hub deliberately does NOT tell us which accounts are
  // Google ones (/login has no captcha; a distinct answer would be an account
  // enumeration oracle), so the hint is shown on every failed sign-in instead.
  const [googleHint, setGoogleHint] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const validate = (emailValue = email) => {
    const e: typeof errors = {};
    if (!EMAIL_RE.test(emailValue)) e.email = t('auth.validation.emailInvalid');
    if (password.length < 6) e.password = t('auth.validation.passwordMin6');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setBanner(null);
    setGoogleHint(false);
    const trimmedEmail = email.trim();
    setEmail(trimmedEmail);
    dispatch(setPendingEmail(trimmedEmail));
    if (!validate(trimmedEmail)) return;
    setSubmitting(true);
    try {
      await dispatch(loginUser({ email: trimmedEmail, password, restaurant_uuid: restaurantUuid })).unwrap();
      dispatch(closeAccountModal());
      if (redirectTo) navigate(redirectTo);
    } catch (code) {
      setBanner(t(ERROR_KEYS[String(code)] ?? ERROR_KEYS.unknown));
      setGoogleHint(code === 'invalid_credentials' && isGoogleLoginEnabled());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-3">
      {banner && (
        <div className="px-3 py-2 bg-yellow-100 border-2 border-ink text-ink text-sm rounded-btn-sm">
          {banner}
          {googleHint && (
            <p className="mt-1 text-xs text-ink/70">{t('auth.errors.loginGoogleHint')}</p>
          )}
        </div>
      )}
      <Field label={t('auth.emailLabel')} error={errors.email}>
        <input
          type="email"
          autoComplete="email"
          value={email}
          onChange={e => { setEmail(e.target.value); dispatch(setPendingEmail(e.target.value)); }}
          className={`w-full px-3 py-2 bg-paper border-2 ${errors.email ? 'border-red-600' : 'border-ink'} rounded-btn-sm text-sm`}
        />
      </Field>
      <Field label={t('auth.passwordLabel')} error={errors.password}>
        <PasswordField value={password} onChange={setPassword} invalid={!!errors.password} />
      </Field>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => dispatch(setMode('forgot'))}
          className="bg-transparent border-0 p-0 text-sm text-ink/70 underline cursor-pointer hover:text-ink"
        >
          {t('auth.forgotLink')}
        </button>
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="w-full py-2.5 bg-ink text-paper font-display rounded-btn shadow-stamp-sm cursor-pointer transition-transform duration-200 hover:-translate-y-[1px] hover:-translate-x-[1px] disabled:opacity-60 disabled:hover:transform-none"
      >
        {submitting ? t('auth.loggingIn') : t('auth.loginCta')}
      </button>
      {isGoogleLoginEnabled() && (
        <>
          <div className="flex items-center gap-3 text-ink/50 text-xs pt-1">
            <hr className="flex-1 border-t border-ink/20" />
            <span>{t('auth.orContinueWith')}</span>
            <hr className="flex-1 border-t border-ink/20" />
          </div>
          <GoogleButton />
        </>
      )}
    </form>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col">
      <span className="text-xs font-sans font-semibold text-ink mb-1">{label}</span>
      {children}
      {error && <span className="text-xs text-red-600 mt-0.5">{error}</span>}
    </label>
  );
}
